import { useBudgetCurrency, useBudgetLocale } from '../budget/budget-store';
import { BudgetCurrency, CURRENCY_SYMBOLS } from '../budget/api/budget-currency';
import { toLocaleString } from '../utils/format';
import { BudgetLocale, LOCALE_FORMATS } from '../budget/api/budget-locale';
/**
 * Global cache for formatted currency strings.
 * Keys are in the format: "{locale}|{currency}|{amount}|{fractionDigits}".
 */
const formattedCurrencyCache = new Map();
/**
 * React hook that returns a currency formatter based on the user's budget locale and currency.
 *
 * The returned formatter will:
 * - Use the budget locale and currency from global state.
 * - Format numbers as currency (or decimal if currency is NONE).
 * - Optionally round to whole numbers when `round` is true.
 * - Cache formatted strings across calls to avoid repeated Intl.NumberFormat invocations.
 *
 * @returns {CurrencyFormatter} A function that formats amounts to localized currency/decimal strings.
 *
 * @example
 * const formatCurrency = useCurrencyFormatter();
 * formatCurrency(1234.56); // "$1,234.56" (for en-US USD)
 * formatCurrency(1234.56, true); // "$1,235"
 */
export function useCurrencyFormatter() {
    const locale = useBudgetLocale();
    const currency = useBudgetCurrency();
    const localeString = toLocaleString(locale);
    return (amount, round = false) => {
        // 1) Normalize amount
        const safeAmount = amount ?? 0;
        // 2) Determine final amount and digits
        const finalAmount = round ? Math.round(safeAmount) : safeAmount;
        const fractionDigits = round ? 0 : 2;
        // 3) Build cache key
        const cacheKey = `${localeString}|${currency}|${finalAmount}|${fractionDigits}`;
        // 4) Return cached result if available
        const cached = formattedCurrencyCache.get(cacheKey);
        if (cached) {
            return cached;
        }
        // 5) Create formatter and format
        const formatter = new Intl.NumberFormat(localeString, currency === BudgetCurrency.NONE
            ? { style: 'decimal', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
            : {
                style: 'currency',
                currency,
                currencyDisplay: 'narrowSymbol',
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            });
        const formatted = formatter.format(finalAmount);
        // 6) Cache and return
        formattedCurrencyCache.set(cacheKey, formatted);
        return formatted;
    };
}
/**
 * Retrieves numeric-format pieces for a given locale and currency.
 *
 * This supports configuring custom numeric inputs (e.g., text fields) to match
 * the display formatting by providing the thousands delimiter, decimal separator,
 * currency symbol, and symbol position.
 *
 * @param {BudgetLocale} localeEnum - The budget locale (e.g., BudgetLocale.EN_US).
 * @param {BudgetCurrency} currency - The budget currency (e.g., BudgetCurrency.USD).
 * @returns {{ delimiter: string; separator: string; symbol: string; isPrefix: boolean }}
 *   - delimiter: The thousands separator (e.g., ",").
 *   - separator: The decimal separator (e.g., ".").
 *   - symbol: The currency symbol (e.g., "$" or empty string for NONE).
 *   - isPrefix: Whether the symbol appears before the number (always true in this app).
 *
 * @example
 * const fmt = getCurrencyFormat(BudgetLocale.EN_US, BudgetCurrency.USD);
 * // => { delimiter: ',', separator: '.', symbol: '$', isPrefix: true }
 */
export function getCurrencyFormat(localeEnum, currency) {
    const localeFormat = LOCALE_FORMATS[localeEnum] || LOCALE_FORMATS[BudgetLocale.EN_US];
    const symbol = CURRENCY_SYMBOLS[currency] || '';
    return {
        delimiter: localeFormat.delimiter,
        separator: localeFormat.separator,
        symbol: symbol,
        isPrefix: true,
    };
}
